import * as React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Wallet, ListTree, Download, ShieldCheck, Menu, X,
  MessageSquareQuote, FileText, Search as SeoIcon, Users, BookOpen, LifeBuoy, Megaphone,
  LayoutDashboard, ScrollText, BarChart3,
} from 'lucide-react';
import { useAuth } from '../core/auth/AuthProvider';
import { ThemeToggle } from '../core/theme/ThemeToggle';
import { PAGE_TRANSITION_V2 } from '../design-system/motion';
import { MeshBackground } from '../design-system/components/MeshBackground';
import { NoiseOverlay } from '../design-system/components/NoiseOverlay';
import { WorqlyMark } from '../design-system/components/WorqlyMark';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

type NavItem = { to: string; icon: React.ReactNode; label: string; end?: boolean; permission?: string };
type Group   = { label: string; items: Array<NavItem> };

const NAV_GROUPS: Group[] = [
  {
    label: 'Overview',
    items: [
      { to: '/admin', icon: <LayoutDashboard className="h-4 w-4" />, label: 'Dashboard', end: true, permission: 'tenants.read' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { to: '/admin/tenants', icon: <Building2 className="h-4 w-4" />, label: 'Tenants', end: true, permission: 'tenants.read' },
    ],
  },
  {
    label: 'Billing',
    items: [
      { to: '/admin/billing/prices',        icon: <Wallet className="h-4 w-4" />,   label: 'Module prices',  permission: 'billing.read' },
      { to: '/admin/billing/subscriptions', icon: <ListTree className="h-4 w-4" />, label: 'Subscriptions',  permission: 'billing.read' },
      { to: '/admin/billing/reports',       icon: <BarChart3 className="h-4 w-4" />, label: 'Billing reports', permission: 'billing.read' },
    ],
  },
  {
    label: 'Site',
    items: [
      { to: '/admin/site/reviews', icon: <MessageSquareQuote className="h-4 w-4" />, label: 'Customer reviews', permission: 'site.reviews.read' },
      { to: '/admin/site/blog',    icon: <FileText className="h-4 w-4" />,           label: 'Blog posts',       permission: 'site.blog.read' },
      { to: '/admin/site/docs',    icon: <BookOpen className="h-4 w-4" />,           label: 'Help center',      permission: 'site.docs.read' },
      { to: '/admin/site/seo',     icon: <SeoIcon className="h-4 w-4" />,            label: 'SEO & analytics',  permission: 'site.seo.read' },
    ],
  },
  {
    label: 'Releases',
    items: [
      { to: '/admin/agent-releases', icon: <Download className="h-4 w-4" />, label: 'Agent installers', permission: 'releases.read' },
    ],
  },
  {
    label: 'Access',
    items: [
      { to: '/admin/access/admins', icon: <Users className="h-4 w-4" />, label: 'Admins & roles', permission: 'admins.read' },
    ],
  },
  {
    label: 'Support',
    items: [
      { to: '/admin/support', icon: <LifeBuoy className="h-4 w-4" />, label: 'Tickets', permission: 'support.read' },
      { to: '/admin/announcements', icon: <Megaphone className="h-4 w-4" />, label: 'Announcements', permission: 'announcements.read' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/audit', icon: <ScrollText className="h-4 w-4" />, label: 'Audit log', permission: 'tenants.read' },
    ],
  },
];

const INITIAL_OF = (s: string) => s ? s.charAt(0).toUpperCase() : '?';

export function SuperAdminShell() {
  const { identity, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const email = identity?.type === 'super_admin' ? identity.email : '';
  const name  = identity?.type === 'super_admin' ? identity.name  : '';

  return (
    <div className="relative min-h-screen flex bg-background">
      <MeshBackground />
      <NoiseOverlay />

      <AnimatePresence>
        {mobileOpen && (
          <motion.button
            type="button"
            aria-label="Close menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 bg-foreground/30 dark:bg-foreground/50 backdrop-blur-sm lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        role="navigation"
        aria-label="Admin navigation"
        className={cn(
          'fixed lg:sticky top-0 z-50 h-screen w-72 shrink-0 flex flex-col',
          'bg-card/85 backdrop-blur-2xl ring-1 ring-border/40 shadow-[8px_0_24px_-12px_oklch(0_0_0/0.06)]',
          'transition-transform duration-300',
          'lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="px-5 pt-5 pb-4 flex items-center justify-between">
          <WorqlyMark size="md" />
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            className="lg:hidden inline-flex h-8 w-8 items-center justify-center rounded-full ring-1 ring-border/60 text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Operator pill */}
        <div className="mx-4 mb-4">
          <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-2xl bg-zinc-950 text-zinc-100 ring-1 ring-zinc-900">
            <ShieldCheck className="h-4 w-4 text-saffron shrink-0" strokeWidth={1.8} />
            <div className="min-w-0 flex-1">
              <p className="text-[11.5px] uppercase tracking-[0.14em] font-semibold text-zinc-400">Operator</p>
              <p className="text-[13px] font-semibold tracking-[-0.005em] text-zinc-100 truncate">Super admin</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-5">
          {NAV_GROUPS.map((g) => (
            <NavGroup key={g.label} group={g} />
          ))}
        </nav>

        <div className="border-t border-border/40 p-4 space-y-3">
          <ThemeToggle />
          <div className="flex items-center gap-3 px-2">
            <div className="h-9 w-9 rounded-full bg-saffron text-zinc-950 flex items-center justify-center text-[13px] font-bold ring-1 ring-saffron/40 shrink-0">
              {INITIAL_OF(name) || INITIAL_OF(email)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold tracking-[-0.005em] text-foreground truncate">{name || email}</p>
              {name && email ? <p className="text-[11px] text-muted-foreground truncate">{email}</p> : null}
            </div>
            <Button
              variant="ghost"
              onClick={() => logout({ redirectTo: '/admin/login' })}
              className="h-auto px-0 text-[12px] font-semibold text-muted-foreground hover:text-foreground"
            >
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30">
          <div className="absolute inset-0 -z-10 bg-background/70 backdrop-blur-xl border-b border-border/40" />
          <div className="h-16 px-4 sm:px-6 lg:px-10 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-border/60 text-foreground"
            >
              <Menu className="h-4 w-4" />
            </button>
            <div className="flex-1" />
            <div className="hidden md:inline-flex h-9 items-center gap-2 px-3 rounded-full ring-1 ring-border/60 bg-card/60">
              <ShieldCheck className="h-3.5 w-3.5 text-saffron" strokeWidth={1.8} />
              <span className="text-[12.5px] font-semibold text-foreground tracking-[-0.005em] truncate max-w-[140px]">
                Admin
              </span>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto" role="main">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              variants={PAGE_TRANSITION_V2}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

function NavGroup({ group }: { group: Group }) {
  // Hook order is stable across renders because `group.items` is a constant
  // module-level array — we call useAdminPermission once per item, every time.
  const { identity } = useAuth();
  const isSuperAdmin = identity?.type === 'super_admin';
  const isOwner = isSuperAdmin && (identity.roles?.includes('owner') ?? false);
  const grants = identity?.type === 'super_admin' ? identity.permissions ?? [] : [];

  const visible = group.items.filter((it) => {
    if (!it.permission) return true;
    if (isOwner) return true;
    return grants.includes(it.permission);
  });
  if (visible.length === 0) return null;

  return (
    <div>
      <p className="px-3 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80 mb-1.5">
        {group.label}
      </p>
      <ul className="space-y-0.5">
        {visible.map((it) => <NavItem key={it.to} {...it} />)}
      </ul>
    </div>
  );
}

function NavItem({ to, icon, label, end }: NavItem) {

  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-3 rounded-xl px-3 py-2 text-[13.5px] tracking-[-0.005em] transition-colors',
            isActive ? 'text-zinc-950 font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 font-medium',
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <motion.span
                layoutId="adminNavActive"
                className="absolute inset-0 -z-10 rounded-xl bg-saffron shadow-[0_4px_12px_-4px_oklch(0.75_0.16_75/0.45)]"
                transition={{ type: 'spring', stiffness: 350, damping: 32 }}
              />
            )}
            <span className="flex h-5 w-5 items-center justify-center">{icon}</span>
            <span className="truncate">{label}</span>
          </>
        )}
      </NavLink>
    </li>
  );
}
